import React, { useState } from 'react';
import { Send, Bot, CheckCircle, XCircle, Loader } from 'lucide-react';
import { cpFetch } from '@/lib/cpClient';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  metadata?: any;
}

interface TemplateResult {
  success: boolean;
  message: string;
  template_id?: string;
  template_name?: string;
  entity_type?: string;
  iku_category?: string;
  unspsc_code?: string | null;
  attributes_count?: number;
  error?: string;
}

export default function AgentChat() {
  const [prompt, setPrompt] = useState('');
  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'assistant',
      content: 'Hello! I can help you create standardized agricultural templates with UNSPSC codes. Try asking me to create templates like:\n\n• "Create a tractor template"\n• "Create a livestock template"\n• "Create a greenhouse sensor template"\n\nI\'ll automatically apply the correct UNSPSC codes and standard agricultural attributes.',
      timestamp: new Date()
    }
  ]);
  const [loading, setLoading] = useState(false);

  const createTemplateFromText = async (text: string): Promise<TemplateResult> => {
    try {
      const resp = await cpFetch('/api/volcano/execute', {
        method: 'POST',
        body: JSON.stringify({
          agent_id: 'contextualizer-pipeline',
          workflow: 'ensure_templates_for_document',
          input: {
            filename: 'manual-text',
            extracted_text: text,
            iku_category: 'iku-products',
          },
          context: { tenant_id: 'dev', user_id: 'dev@devcockpit.ai' },
        }),
      });

      const internal = resp?.internal_data || resp?.output || resp?.result || null;
      const templateId = internal?.template_id || null;
      const templateName = internal?.template_name || null;
      const entityType = internal?.entity_type || null;
      const ikuCategory = internal?.iku_category || null;
      const unspsc = internal?.unspsc_code ?? null;
      const attrs = internal?.template_attributes || [];

      return {
        success: true,
        message: `Template ensured${templateName ? `: ${templateName}` : ''}${unspsc ? ` (UNSPSC ${unspsc})` : ''}`,
        template_id: templateId || undefined,
        template_name: templateName || undefined,
        entity_type: entityType || undefined,
        iku_category: ikuCategory || undefined,
        unspsc_code: unspsc,
        attributes_count: Array.isArray(attrs) ? attrs.length : undefined,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error?.message || 'Template creation failed',
        message: `Failed to create template: ${error?.message || 'Unknown error'}`,
      };
    }
  };

  const sendToAgent = async () => {
    if (!prompt.trim()) return;
    
    setLoading(true);
    const userMessage: Message = { 
      role: 'user', 
      content: prompt, 
      timestamp: new Date() 
    };
    setMessages(prev => [...prev, userMessage]);
    
    try {
      const result = await createTemplateFromText(prompt);
      const assistantMessage: Message = {
        role: 'assistant',
        content: result.message,
        timestamp: new Date(),
        metadata: result
      };
      setMessages(prev => [...prev, assistantMessage]);
      
    } catch (error: any) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `Error: ${error.message}`,
        timestamp: new Date()
      }]);
    } finally {
      setLoading(false);
      setPrompt('');
    }
  };

  const formatTimestamp = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg">
        {/* Header */}
        <div className="border-b border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Bot className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Agricultural Template Agent</h1>
              <p className="text-sm text-gray-600">Create standardized templates with UNSPSC codes</p>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="h-96 overflow-y-auto p-4 space-y-4">
          {messages.map((msg, idx) => (
            <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                msg.role === 'user' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-100 text-gray-900'
              }`}>
                <div className="whitespace-pre-wrap">{msg.content}</div>
                
                {/* Show template creation results */}
                {msg.metadata && (
                  <div className="mt-2 pt-2 border-t border-gray-300">
                    <div className="flex items-center gap-2 text-xs">
                      {msg.metadata.success ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-600" />
                      )}
                      {msg.metadata.success ? (
                        <span className="text-green-700">
                          {msg.metadata.template_name ? `Template: ${msg.metadata.template_name}` : 'Template created'}
                          {msg.metadata.unspsc_code ? ` | UNSPSC: ${msg.metadata.unspsc_code}` : ''}
                          {typeof msg.metadata.attributes_count === 'number' ? ` | Attributes: ${msg.metadata.attributes_count}` : ''}
                        </span>
                      ) : (
                        <span className="text-red-700">Creation failed</span>
                      )}
                    </div>
                  </div>
                )}
                
                <div className="text-xs opacity-70 mt-1">
                  {formatTimestamp(msg.timestamp)}
                </div>
              </div>
            </div>
          ))}
          
          {loading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 text-gray-900 px-4 py-2 rounded-lg flex items-center gap-2">
                <Loader className="w-4 h-4 animate-spin" />
                Creating template...
              </div>
            </div>
          )}
        </div>
        
        {/* Input */}
        <div className="border-t border-gray-200 p-4">
          <div className="flex gap-2">
            <input
              type="text"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && !loading && sendToAgent()}
              placeholder="Create a tractor template..."
              disabled={loading}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <button
              onClick={sendToAgent}
              disabled={loading || !prompt.trim()}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Send className="w-4 h-4" />
              Send
            </button>
          </div>
          
          {/* Quick suggestions */}
          <div className="mt-2 flex flex-wrap gap-2">
            {['Create a tractor template', 'Create a livestock template', 'Create a sensor template'].map((suggestion) => (
              <button
                key={suggestion}
                onClick={() => setPrompt(suggestion)}
                disabled={loading}
                className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 disabled:opacity-50"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
